import { FileUploadUtility, ISignedURLResponse } from './file-upload-utility';
import { FileUploadType } from './upload-types';

/* ============================================
   Type Definitions
   ============================================ */

interface IDocumentsUploadElements {
    leaseFileInput: HTMLInputElement | null;
    paymentFileInput: HTMLInputElement;
    submitButton: HTMLButtonElement;
    leaseUploadSection: HTMLElement | null;
    leaseFileButton: HTMLButtonElement | null;
    leaseUploadCard: HTMLElement | null;
    paymentFileButton: HTMLButtonElement | null;
    paymentUploadCard: HTMLElement | null;
    existingLeaseCard: HTMLElement | null;
    existingLeaseSelectedCard: HTMLElement | null;
    existingLeaseIdInput: HTMLInputElement | null;
    loadingIndicator: HTMLElement;
}

interface IUploadState {
    leaseFile: File | null;
    paymentFile: File | null;
    useExistingLease: boolean;
    existingLeaseId: string | null;
    isRentReimbursement: boolean;
}

type FileType = 'lease' | 'payment';

/* ============================================
   DocumentsUpload Class
   ============================================ */

/**
 * Handles the unified document upload page for lease and proof of payment files.
 * Manages UI state transitions between default, selected, and error states.
 */
export class DocumentsUpload {
    private static readonly DIALOG_IDS = {
        loading: 'uploadLoadingIndicator',
        success: 'uploadSuccessDialog',
        error: 'uploadFailureDialog'
    } as const;

    private elements: IDocumentsUploadElements;
    private state: IUploadState;

    constructor() {
        this.elements = this.getElements();
        this.state = this.initializeState();
        this.bindEvents();
    }

    /* ============================================
       Initialization
       ============================================ */

    private getElements(): IDocumentsUploadElements {
        const paymentFileInput = document.getElementById(
            'documentsPaymentFileInput'
        ) as HTMLInputElement;
        const submitButton = document.getElementById(
            'uploadSubmitButton'
        ) as HTMLButtonElement;
        const loadingIndicator = document.getElementById(
            DocumentsUpload.DIALOG_IDS.loading
        ) as HTMLElement;

        if (!paymentFileInput || !submitButton || !loadingIndicator) {
            throw new Error(
                'Required DOM elements for DocumentsUpload not found.'
            );
        }

        return {
            leaseFileInput: document.getElementById(
                'documentsLeaseFileInput'
            ) as HTMLInputElement | null,
            paymentFileInput,
            submitButton,
            leaseUploadSection: document.getElementById('leaseUploadSection'),
            leaseFileButton: document.getElementById(
                'leaseFileButton'
            ) as HTMLButtonElement | null,
            leaseUploadCard: document.getElementById('leaseUploadCard'),
            paymentFileButton: document.getElementById(
                'paymentFileButton'
            ) as HTMLButtonElement | null,
            paymentUploadCard: document.getElementById('paymentUploadCard'),
            existingLeaseCard: document.getElementById('existingLeaseCard'),
            existingLeaseSelectedCard: document.getElementById(
                'existingLeaseSelectedCard'
            ),
            existingLeaseIdInput: document.getElementById(
                'existingLeaseId'
            ) as HTMLInputElement | null,
            loadingIndicator
        };
    }

    private initializeState(): IUploadState {
        return {
            leaseFile: null,
            paymentFile: null,
            useExistingLease: false,
            existingLeaseId: this.elements.existingLeaseIdInput?.value || null,
            isRentReimbursement: this.elements.leaseFileInput !== null
        };
    }

    private bindEvents(): void {
        this.elements.leaseFileInput?.addEventListener('change', e => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) this.onFileSelected('lease', file);
        });

        this.elements.paymentFileInput.addEventListener('change', e => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) this.onFileSelected('payment', file);
        });
    }

    /* ============================================
       Public Methods (called from onclick handlers)
       ============================================ */

    /** Triggers file input for lease upload */
    public selectLeaseFile(): void {
        this.elements.leaseFileInput?.click();
    }

    /** Triggers file input for payment upload */
    public selectPaymentFile(): void {
        this.elements.paymentFileInput.click();
    }

    /** Uses the existing lease instead of uploading a new one */
    public selectExistingLease(): void {
        if (!this.state.existingLeaseId) return;

        this.state.useExistingLease = true;
        this.state.leaseFile = null;
        this.showExistingLeaseSelected();
        this.updateSubmitButtonState();
    }

    /** Submits the uploaded documents */
    public async submit(): Promise<void> {
        if (this.elements.submitButton.disabled) return;

        this.showLoading(true);

        try {
            const leaseDocId = await this.processLeaseUpload();
            await this.processPaymentUpload(leaseDocId);
            this.showLoading(false);
            this.showDialog('success');
        } catch (error) {
            console.error('Upload failed:', error);
            this.showLoading(false);
            this.showDialog('error');
        }
    }

    /* ============================================
       File Selection Handlers
       ============================================ */

    private onFileSelected(fileType: FileType, file: File): void {
        if (fileType === 'lease') {
            this.state.leaseFile = file;
            this.state.useExistingLease = false;
            this.showLeaseSelected();
        } else {
            this.state.paymentFile = file;
            this.showPaymentSelected();
        }
        this.updateSubmitButtonState();
    }

    private handleDeleteFile(fileType: FileType): void {
        if (fileType === 'lease') {
            this.clearLeaseFile();
        } else {
            this.clearPaymentFile();
        }
        this.updateSubmitButtonState();
    }

    private clearLeaseFile(): void {
        this.state.leaseFile = null;
        this.state.useExistingLease = false;

        if (this.elements.leaseFileInput) {
            this.elements.leaseFileInput.value = '';
        }

        this.resetUploadCard('lease');

        // Restore existing lease card if user has one
        if (this.state.existingLeaseId) {
            this.setElementVisibility(this.elements.existingLeaseCard, true);
            this.setElementVisibility(
                this.elements.existingLeaseSelectedCard,
                false
            );
            this.setElementVisibility(this.elements.leaseUploadSection, false);
        }
    }

    private clearPaymentFile(): void {
        this.state.paymentFile = null;
        this.elements.paymentFileInput.value = '';
        this.resetUploadCard('payment');
    }

    /* ============================================
       UI State Management
       ============================================ */

    private showLeaseSelected(): void {
        this.setElementVisibility(this.elements.existingLeaseCard, false);
        this.setElementVisibility(
            this.elements.existingLeaseSelectedCard,
            false
        );
        this.setElementVisibility(this.elements.leaseUploadSection, true);
        this.showSelectedState(
            this.elements.leaseUploadCard,
            this.elements.leaseFileButton,
            'lease'
        );
    }

    private showPaymentSelected(): void {
        this.showSelectedState(
            this.elements.paymentUploadCard,
            this.elements.paymentFileButton,
            'payment'
        );
    }

    private showExistingLeaseSelected(): void {
        this.setElementVisibility(this.elements.existingLeaseCard, false);
        this.setElementVisibility(
            this.elements.existingLeaseSelectedCard,
            true
        );
    }

    private showSelectedState(
        uploadCard: HTMLElement | null,
        fileButton: HTMLButtonElement | null,
        fileType: FileType
    ): void {
        if (!uploadCard) return;

        // Hide default state, show selected state
        this.setElementVisibility(fileButton, false);

        const selectedState = uploadCard.querySelector(
            '.upload-card-selected-state'
        ) as HTMLElement;
        if (!selectedState) return;

        selectedState.hidden = false;
        selectedState.classList.add('is-selected');

        // Update filename display
        const file =
            fileType === 'lease'
                ? this.state.leaseFile
                : this.state.paymentFile;
        const filenameDisplay = selectedState.querySelector(
            '[data-filename-display]'
        ) as HTMLElement;
        if (filenameDisplay && file) {
            filenameDisplay.textContent = file.name;
        }

        // Bind delete button
        const deleteButton = selectedState.querySelector(
            '[data-delete-button]'
        ) as HTMLButtonElement;
        if (deleteButton) {
            deleteButton.onclick = (e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                this.handleDeleteFile(fileType);
            };
        }
    }

    private resetUploadCard(fileType: FileType): void {
        const { card, button } =
            fileType === 'lease'
                ? {
                      card: this.elements.leaseUploadCard,
                      button: this.elements.leaseFileButton
                  }
                : {
                      card: this.elements.paymentUploadCard,
                      button: this.elements.paymentFileButton
                  };

        this.setElementVisibility(button, true);

        const selectedState = card?.querySelector(
            '.upload-card-selected-state'
        ) as HTMLElement;
        if (selectedState) {
            selectedState.hidden = true;
            selectedState.classList.remove('is-selected');
        }
    }

    private updateSubmitButtonState(): void {
        const hasLease =
            this.state.leaseFile !== null || this.state.useExistingLease;
        const hasPayment = this.state.paymentFile !== null;
        const isReady = this.state.isRentReimbursement
            ? hasLease && hasPayment
            : hasPayment;

        this.elements.submitButton.disabled = !isReady;
    }

    /* ============================================
       Upload Processing
       ============================================ */

    private async processLeaseUpload(): Promise<string | null> {
        if (!this.state.isRentReimbursement) return null;

        if (this.state.useExistingLease && this.state.existingLeaseId) {
            return this.state.existingLeaseId;
        }

        if (this.state.leaseFile) {
            const leaseDocId = await this.uploadFile(
                this.state.leaseFile,
                FileUploadType.Lease
            );
            if (!leaseDocId) throw new Error('Failed to upload lease');
            return leaseDocId;
        }

        return null;
    }

    private async processPaymentUpload(
        leaseDocId: string | null
    ): Promise<void> {
        if (!this.state.paymentFile) return;

        const paymentDocId = await this.uploadFile(
            this.state.paymentFile,
            FileUploadType.ProofOfPayment,
            leaseDocId
        );

        if (!paymentDocId) throw new Error('Failed to upload proof of payment');
    }

    private async uploadFile(
        file: File,
        uploadType: FileUploadType,
        leaseDocId?: string | null
    ): Promise<string | null> {
        try {
            const utility = new FileUploadUtility(leaseDocId);
            const response: ISignedURLResponse =
                await utility.getSignedURLForType(file, uploadType);

            if (!response.url) {
                console.error('Failed to get signed URL for', uploadType);
                return null;
            }

            await utility.uploadFileToSignedURL(
                file,
                response.url,
                response.maxSize
            );
            return response.fileID || null;
        } catch (error) {
            console.error('Error uploading', uploadType, ':', error);
            return null;
        }
    }

    /* ============================================
       Dialog & Loading Management
       ============================================ */

    private showLoading(visible: boolean): void {
        this.elements.loadingIndicator.hidden = !visible;
    }

    private showDialog(type: 'success' | 'error'): void {
        const dialogId = DocumentsUpload.DIALOG_IDS[type];
        const dialog = document.getElementById(
            dialogId
        ) as HTMLDialogElement | null;

        if (!dialog) return;

        if (type === 'error') {
            dialog.addEventListener('close', () => this.resetUploadState(), {
                once: true
            });
        }

        dialog.showModal();
    }

    private resetUploadState(): void {
        this.state.leaseFile = null;
        this.state.paymentFile = null;
        this.state.useExistingLease = false;

        if (this.elements.leaseFileInput) {
            this.elements.leaseFileInput.value = '';
        }
        this.elements.paymentFileInput.value = '';

        // Reload page for fresh state if user has existing lease
        if (this.state.existingLeaseId && this.elements.existingLeaseCard) {
            window.location.reload();
            return;
        }

        this.resetUploadCard('lease');
        this.resetUploadCard('payment');
        this.updateSubmitButtonState();
    }

    /* ============================================
       Utility Methods
       ============================================ */

    private setElementVisibility(
        element: HTMLElement | null,
        visible: boolean
    ): void {
        if (element) {
            element.hidden = !visible;
        }
    }
}

/* ============================================
   Global Instance & Initialization
   ============================================ */

declare global {
    interface Window {
        documentsUpload: DocumentsUpload;
    }
}

function initializeDocumentsUploadIfNeeded(): void {
    if (document.getElementById('uploadSubmitButton')) {
        window.documentsUpload = new DocumentsUpload();
    }
}

// Initialize on page load
document.addEventListener(
    'DOMContentLoaded',
    initializeDocumentsUploadIfNeeded
);

// Re-initialize after HTMX content swaps
document.body.addEventListener(
    'htmx:afterSwap',
    initializeDocumentsUploadIfNeeded
);
